import api from "./api";
import { getStoredClientId, getStoredUser } from "../utils/tokenUtils";

/**
 * Sanitize object to remove circular references and non-serializable values
 * @param {Object} obj - Object to sanitize
 * @returns {Object} - Sanitized object with only plain values
 */
const sanitizePayload = (obj) => {
  const seen = new WeakSet();

  const sanitize = (value) => {
    // Handle null/undefined
    if (value === null || value === undefined) {
      return null;
    }

    // Handle primitives
    if (typeof value !== "object") {
      return value;
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return value.map(sanitize);
    }

    // Handle Date objects
    if (value instanceof Date) {
      return value.toISOString();
    }

    // Skip circular references
    if (seen.has(value)) {
      return "[Circular]";
    }

    // Skip DOM elements and React elements
    if (value instanceof HTMLElement || value instanceof Element) {
      return null;
    }

    // Check for React Fiber nodes (common pattern)
    if (
      value.constructor &&
      value.constructor.name &&
      (value.constructor.name.includes("Fiber") ||
        value.constructor.name.includes("React") ||
        value.__reactFiber$ ||
        value.__reactInternalInstance)
    ) {
      return null;
    }

    seen.add(value);

    // Handle plain objects
    const result = {};
    for (const key in value) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        // Skip React internal properties
        if (
          key.startsWith("__react") ||
          key.startsWith("__") ||
          key === "stateNode"
        ) {
          continue;
        }
        try {
          result[key] = sanitize(value[key]);
        } catch (e) {
          // Skip properties that cause errors
          continue;
        }
      }
    }

    seen.delete(value);
    return result;
  };

  return sanitize(obj);
};

const sectionService = {
  /**
   * Get all sections for a project
   */
  getSections: async (projectId, filters = {}) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      project_id: projectId,
      client_id: storedClientId || user?.client_id,
      parent_section_id: filters.parent_section_id,
      search: filters.search || undefined,
      branch_id: filters.branch_id || undefined,
    };
    const response = await api.post("api/sections/list", payload);
    return response.data;
  },

  /**
   * Get section tree structure
   */
  getSectionTree: async (
    projectId,
    branchId = null,
    suiteId = null,
    baselineId = null,
  ) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      project_id: projectId,
      client_id: storedClientId || user?.client_id,
      branch_id: branchId || undefined,
      suite_id: suiteId || undefined,
      baseline_id: baselineId || undefined,
    };
    const response = await api.post("api/sections/tree", payload);
    return response.data;
  },

  /**
   * Get section by ID
   */
  getSectionById: async (sectionId) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      section_id: sectionId,
      client_id: storedClientId || user?.client_id,
    };
    const response = await api.post("api/sections/get", payload);
    return response.data;
  },

  /**
   * Create a new section with support for subsection creation
   */
  createSection: async (sectionData) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();

    // Sanitize payload to only include plain data (remove React elements, circular refs, etc.)
    // Extract only the fields we need and ensure they're plain values
    const sanitizedData = {
      project_id: sectionData.project_id
        ? String(sectionData.project_id)
        : null,
      name: sectionData.name ? String(sectionData.name).trim() : null,
      description: sectionData.description
        ? String(sectionData.description).trim()
        : null,
      parent_section_id: sectionData.parent_section_id
        ? String(sectionData.parent_section_id)
        : null,
      parent_folder_id:
        sectionData.parent_folder_id || sectionData.parent_section_id
          ? String(
              sectionData.parent_folder_id || sectionData.parent_section_id,
            )
          : null,
      created_by: sectionData.created_by
        ? String(sectionData.created_by)
        : null,
      branch_id: sectionData.branch_id ? String(sectionData.branch_id) : null,
      baseline_id: sectionData.baseline_id
        ? String(sectionData.baseline_id)
        : null,
      suite_id: sectionData.suite_id ? String(sectionData.suite_id) : null,
      // First-time flow specific fields
      subsectionName: sectionData.subsectionName
        ? String(sectionData.subsectionName).trim()
        : null,
      targetLocation: sectionData.targetLocation || null,
    };

    // Remove any null/undefined values and sanitize to remove any remaining circular refs
    const cleanData = sanitizePayload(sanitizedData);

    // Final payload with only serializable values
    const payload = {
      project_id: cleanData.project_id,
      name: cleanData.name,
      description: cleanData.description,
      parent_section_id: cleanData.parent_section_id,
      parent_folder_id: cleanData.parent_folder_id,
      created_by: cleanData.created_by,
      branch_id: cleanData.branch_id,
      baseline_id: cleanData.baseline_id,
      suite_id: cleanData.suite_id,
      client_id: storedClientId || user?.client_id,
      // Include first-time flow fields if present
      subsectionName: cleanData.subsectionName,
      targetLocation: cleanData.targetLocation,
    };

    // Remove null/undefined values to keep payload clean
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    try {
      const response = await api.post("api/sections/create", payload);
      return response.data;
    } catch (error) {
      // Extract error message from response
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to create section";
      throw new Error(errorMessage);
    }
  },

  /**
   * Create section with subsection support (for first-time flow)
   */
  createSectionWithSubsection: async (sectionData) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();

    try {
      // First create the parent section
      const parentPayload = {
        project_id: sectionData.project_id,
        name: sectionData.name,
        description: sectionData.description,
        parent_section_id: sectionData.parent_section_id || null,
        created_by: user?.user_id,
        branch_id: sectionData.branch_id || null,
        baseline_id: sectionData.baseline_id || null,
        suite_id: sectionData.suite_id || null,
        client_id: storedClientId || user?.client_id,
      };

      const parentResponse = await api.post(
        "api/sections/create",
        parentPayload,
      );
      const parentSection = parentResponse.data;

      // If subsection is requested, create it under the parent
      if (sectionData.subsectionName && sectionData.subsectionName.trim()) {
        const subsectionPayload = {
          project_id: sectionData.project_id,
          name: sectionData.subsectionName.trim(),
          description: null,
          parent_section_id: parentSection.section_id || parentSection.id,
          created_by: user?.user_id,
          branch_id: sectionData.branch_id || null,
          baseline_id: sectionData.baseline_id || null,
          suite_id: sectionData.suite_id || null,
          client_id: storedClientId || user?.client_id,
        };

        const subsectionResponse = await api.post(
          "api/sections/create",
          subsectionPayload,
        );
        const subsection = subsectionResponse.data;

        // Return both sections with target information
        return {
          parentSection,
          subsection,
          targetSectionId:
            sectionData.targetLocation === "subsection"
              ? subsection.section_id || subsection.id
              : parentSection.section_id || parentSection.id,
        };
      }

      // Return just the parent section
      return {
        parentSection,
        subsection: null,
        targetSectionId: parentSection.section_id || parentSection.id,
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to create section";
      throw new Error(errorMessage);
    }
  },

  /**
   * Update a section
   */
  updateSection: async (sectionId, updates) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();

    // Sanitize payload to only include plain data
    const sanitizedUpdates = {
      name: updates.name ? String(updates.name).trim() : null,
      description: updates.description
        ? String(updates.description).trim()
        : null,
      parent_section_id: updates.parent_section_id
        ? String(updates.parent_section_id)
        : null,
      parent_folder_id:
        updates.parent_folder_id || updates.parent_section_id
          ? String(updates.parent_folder_id || updates.parent_section_id)
          : null,
    };

    // Remove any undefined values and sanitize to remove any remaining circular refs
    const cleanUpdates = sanitizePayload(sanitizedUpdates);

    // Final payload with only serializable values
    const payload = {
      section_id: String(sectionId),
      name: cleanUpdates.name,
      description: cleanUpdates.description,
      parent_section_id: cleanUpdates.parent_section_id,
      parent_folder_id: cleanUpdates.parent_folder_id,
      client_id: storedClientId || user?.client_id,
    };

    // Remove null/undefined values to keep payload clean
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    try {
      const response = await api.post("api/sections/update", payload);
      return response.data;
    } catch (error) {
      // Extract error message from response
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to update section";
      throw new Error(errorMessage);
    }
  },

  /**
   * Delete a section
   */
  deleteSection: async (sectionId, options = {}) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      section_id: sectionId,
      client_id: storedClientId || user?.client_id,
      ...options,
    };
    const response = await api.post("api/sections/delete", payload);
    return response.data;
  },

  /**
   * Move test cases to section
   */
  moveTestCasesToSection: async (testCaseIds, sectionId = null) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();
    const payload = {
      test_case_ids: testCaseIds,
      section_id: sectionId,
      client_id: storedClientId || user?.client_id,
    };
    const response = await api.post("api/sections/move-test-cases", payload);
    return response.data;
  },

  // Legacy methods for backward compatibility (will be removed later)
  getFolders: async (projectId, filters = {}) => {
    return sectionService.getSections(projectId, filters);
  },
  getFolderTree: async (projectId) => {
    return sectionService.getSectionTree(projectId);
  },
  getFolderById: async (folderId) => {
    return sectionService.getSectionById(folderId);
  },
  createFolder: async (folderData) => {
    return sectionService.createSection(folderData);
  },
  updateFolder: async (folderId, updates) => {
    return sectionService.updateSection(folderId, updates);
  },
  deleteFolder: async (folderId) => {
    return sectionService.deleteSection(folderId);
  },
  moveTestCasesToFolder: async (testCaseIds, folderId = null) => {
    return sectionService.moveTestCasesToSection(testCaseIds, folderId);
  },
};

export default sectionService;
